// ABOUTME: The fetch client for the Jobs page's feed on pro-core (GET /talent/feed, #2094): bearer token,
// ABOUTME: Accept-Language, and the 404 that says the feed is off for this talent mapped to its own code.
import { ApiError, ResponseError } from 'utils/api';
import { env } from '../../constants';
import { FEED_API_URL, FEED_UNAVAILABLE_ERROR_CODE } from './constants';
import { Feed, FeedResponse, FeedSection, FeedTab } from './types';

const readError = async (response: Response): Promise<ResponseError> => {
  try {
    const json = (await response.json()) as Partial<ResponseError>;
    return { code: json.code ?? '', values: json.values ?? {} };
  } catch {
    return { code: '', values: {} };
  }
};

/**
 * GET /talent/feed?tab=…: the tab's sections in the order pro-core decided, empty ones left out. A 404 is the feed
 * being off for this talent, which the Jobs page answers by showing today's page; a 401 reloads so Auth0 renews.
 */
export const fetchFeed = async (
  token: string,
  language: string,
  tab: FeedTab
): Promise<Feed> => {
  const response = await fetch(
    `${env.VITE_API_BASE_URL}/${FEED_API_URL}?tab=${tab}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Accept-Language': language
      }
    }
  );

  if (!response.ok) {
    if (response.status === 401) window.location.reload();
    if (response.status === 404) {
      throw new ApiError({ code: FEED_UNAVAILABLE_ERROR_CODE, values: {} });
    }
    throw new ApiError(await readError(response));
  }

  const json = (await response.json()) as FeedResponse;
  // non_empty: a feed without sections arrives without the list.
  return {
    ...json.data,
    sections: (json.data.sections ?? []).map(withLists)
  };
};

/**
 * pro-core serialises with non_empty, so an empty list arrives as no list at all where its record does not say
 * otherwise. The job's `beforeStart` is such a list, and empty until a checklist exists, so it is defaulted here and the
 * card never has to ask. The listings section and its items keep their empty lists on the wire, defaulted all the same.
 */
const withLists = (section: FeedSection): FeedSection => {
  switch (section.kind) {
    case 'EMPLOYMENT':
      return {
        ...section,
        items: (section.items ?? []).map(item => ({
          ...item,
          beforeStart: item.beforeStart ?? []
        }))
      };
    case 'LISTINGS':
      return {
        ...section,
        items: (section.items ?? []).map(item => ({
          ...item,
          badges: item.badges ?? []
        }))
      };
    default:
      return section;
  }
};
